// Taken from mm-execution-engine/controllers/PasswordController.js:91 updatePassword(targets, password, confirmation_password, old_password)
/* returns in the form of
[ {ip:x.x.x.x, defaultTargets: targets}, ... ]
where targets could be:
    - null, failed to reach the server
    - { authentication: message }, failed to authentificate with the server, wrong mm password
    - { bad_request: message }, any error from the server, ex: password too short
    - { mm : true | undefined | message, pendant;..., cloud9:... }
        where:
            - true: succesfully set the password
            - null: could not reach the target (key could be missing)
            - message: error message
*/

export interface SetPasswordResponse {
  status?: SetPasswordStatus[] | undefined
}

type SetPasswordStatus = SetPasswordServerUnreachableError | SetPasswordAuthenticationError | SetPasswordBadRequestError | SetPasswordResult;

interface SetPasswordServerUnreachableError {
  ip: string
  defaultTargets: null
}

interface SetPasswordAuthenticationError {
  ip: string
  defaultTargets: { authentication: string }
}

interface SetPasswordBadRequestError {
  ip: string
  defaultTargets: { bad_request: string }
}

interface SetPasswordResult {
  ip: string
  defaultTargets: DefaultTargets
}

interface DefaultTargets {
  mm: DefaultTargetResult
  cloud9: DefaultTargetResult
  pendant: DefaultTargetResult
}

type DefaultTargetResult = Success | TargetUnreachable | ErrorMessage;
type Success = true;
type TargetUnreachable = null;
type ErrorMessage = string;

export function parseSetPasswordResponseErrors (setPasswordResult?: SetPasswordResponse): string[] {
  const setPasswordResultStatuses = setPasswordResult?.status;
  if (setPasswordResultStatuses === undefined) {
    return [];
  }

  const hasManyTargets = setPasswordResultStatuses.length > 1;
  const errorMessages: string[] = [];

  for (const passwordResultStatus of setPasswordResultStatuses) {
    if (isSetPasswordServerUnreachableError(passwordResultStatus)) {
      errorMessages.push(parseServerUnreachableError(passwordResultStatus, hasManyTargets));
    }
    else if (isSetPasswordAuthenticationError(passwordResultStatus)) {
      errorMessages.push(parseSetPasswordAuthenticationError(passwordResultStatus, hasManyTargets));
    }
    else if (isSetPasswordBadRequestError(passwordResultStatus)) {
      errorMessages.push(parseSetPasswordBadRequestError(passwordResultStatus, hasManyTargets));
    }
    else if (isSetPasswordResult(passwordResultStatus)) {
      errorMessages.push(...parseSetPasswordResultErrors(passwordResultStatus, hasManyTargets));
    }
    else {
      console.error('Could not determine the type of passwordResultStatus', JSON.stringify(passwordResultStatus));
    }
  }

  return errorMessages;
}

function isSetPasswordServerUnreachableError (setPasswordStatus: SetPasswordStatus): setPasswordStatus is SetPasswordServerUnreachableError {
  return setPasswordStatus.defaultTargets === null;
}

function parseServerUnreachableError (setPasswordResultStatus: SetPasswordServerUnreachableError, showIpInError: boolean): string {
  const failedIpMessage = showIpInError
    ? ` for IP ${setPasswordResultStatus.ip}`
    : '';

  return `Failed to set password${failedIpMessage}. Could not reach the server.`;
}

function isSetPasswordAuthenticationError (setPasswordStatus: SetPasswordStatus): setPasswordStatus is SetPasswordAuthenticationError {
  if (setPasswordStatus.defaultTargets === null) {
    return false;
  }

  return 'authentication' in setPasswordStatus.defaultTargets;
}

function parseSetPasswordAuthenticationError (setPasswordAuthenticationError: SetPasswordAuthenticationError, showIpInError: boolean): string {
  const failedIpMessage = showIpInError
    ? ` for IP ${setPasswordAuthenticationError.ip}`
    : '';

  return `Failed to set password${failedIpMessage}. ${JSON.stringify(setPasswordAuthenticationError.defaultTargets)}`;
}

function isSetPasswordBadRequestError (setPasswordStatus: SetPasswordStatus): setPasswordStatus is SetPasswordBadRequestError {
  if (setPasswordStatus.defaultTargets === null) {
    return false;
  }

  return 'bad_request' in setPasswordStatus.defaultTargets;
}

function parseSetPasswordBadRequestError (setPasswordBadRequestError: SetPasswordBadRequestError, showIpInError: boolean): string {
  const failedIpMessage = showIpInError
    ? ` for IP ${setPasswordBadRequestError.ip}`
    : '';

  return `Failed to set password${failedIpMessage}. ${JSON.stringify(setPasswordBadRequestError.defaultTargets)}`;
}

function isSetPasswordResult (setPasswordStatus: SetPasswordStatus): setPasswordStatus is SetPasswordResult {
  if (setPasswordStatus.defaultTargets === null) {
    return false;
  }

  return 'mm' in setPasswordStatus.defaultTargets && 'cloud9' in setPasswordStatus.defaultTargets && 'pendant' in setPasswordStatus.defaultTargets;
}

function parseSetPasswordResultErrors (setPasswordResult: SetPasswordResult, showIpInError: boolean): string[] {
  const defaultTargets = setPasswordResult.defaultTargets;
  const errors: string[] = [];

  for (const target of Object.keys(defaultTargets) as Array<keyof DefaultTargets>) {
    const targetResult = defaultTargets[target];
    if (isTargetUnreachable(targetResult)) {
      errors.push(parseTargetUnreachable(target, setPasswordResult.ip, showIpInError));
    }
    else if (isErrorMessage(targetResult)) {
      errors.push(parseErrorMessage(target, targetResult, setPasswordResult.ip, showIpInError));
    }
  }

  return errors;
}

function isTargetUnreachable (defaultTargetResult: DefaultTargetResult): defaultTargetResult is TargetUnreachable {
  return defaultTargetResult === null;
}

function parseTargetUnreachable (target: keyof DefaultTargets, ip: string, showIpInError: boolean): string {
  const failedIpMessage = showIpInError
    ? ` for IP ${ip}`
    : '';

  return `Failed to set password for "${target}"${failedIpMessage}. Reason: Could not reach the target`;
}

function isErrorMessage (defaultTargetResult: DefaultTargetResult): defaultTargetResult is ErrorMessage {
  return typeof defaultTargetResult === 'string';
}

function parseErrorMessage (target: keyof DefaultTargets, error: string, ip: string, showIpInError: boolean): string {
  const failedIpMessage = showIpInError
    ? ` for IP ${ip}`
    : '';

  return `Failed to set password for "${target}"${failedIpMessage}. Reason: ${error}`;
}
